import { useCallback } from 'react';
import * as React from 'react';
import styled from '@emotion/styled';
import * as d3 from 'd3';

import { tokens } from '@hotjar/hotsauce';
import { getGaugeColorScale } from '@hotjar/webapp/features/SamplingWidget/SessionTrafficCoverage/utils';

const Svg = styled.svg<{ size: number }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;

interface Props {
  coveragePercentage: number;
  isCoverageMeasureComplete: boolean;
  disableAnimation?: boolean;
  isDropDownShown: boolean;
}

interface ArcDatum {
  endAngle: number;
}

export const TrafficGaugeSumUp = ({
  coveragePercentage,
  isCoverageMeasureComplete,
  disableAnimation = false,
  isDropDownShown,
}: Props): JSX.Element => {
  const textColor = isCoverageMeasureComplete
    ? tokens.color.text_default
    : tokens.color.text_disabled;
  const size = 85;
  const arcWidth = 10;
  const outerRadius = size / 2;
  const innerRadius = outerRadius - arcWidth;
  const cornerRadius = (outerRadius - innerRadius) / 2;
  const gRef = useCallback(
    (node) => {
      if (node !== null) {
        const radScale = d3
          .scaleLinear()
          .domain([0, 100])
          .range([0, 2 * Math.PI]);

        const colorScale = getGaugeColorScale();

        const gaugeColor = isCoverageMeasureComplete
          ? colorScale(coveragePercentage)
          : tokens.color.neutral_700;

        const arc = d3
          .arc<ArcDatum>()
          .innerRadius(innerRadius)
          .outerRadius(outerRadius)
          .cornerRadius(cornerRadius)
          .startAngle(0);

        const gSelection = d3.select(node);
        const pathSelection = gSelection
          .select<SVGPathElement>('path')
          .datum({ endAngle: disableAnimation ? radScale(coveragePercentage) : 0 })
          .attr('d', arc)
          .style('fill', gaugeColor);

        const textSelection = gSelection
          .select<SVGTextElement>('text')
          .datum({ value: disableAnimation ? coveragePercentage : 0 })
          .text((d) => `${d.value}%`);

        if (!disableAnimation && isDropDownShown) {
          const durationMs = 800;
          // Method inspired by http://bl.ocks.org/mbostock/5100636
          const arcTween = (d: ArcDatum) => {
            const interpolate = d3.interpolate(d.endAngle, radScale(coveragePercentage));
            return (t: number) => {
              const newD = {
                endAngle: interpolate(t),
              };
              return arc(newD) || '';
            };
          };
          pathSelection.transition().duration(durationMs).attrTween('d', arcTween);

          textSelection
            .transition()
            .duration(durationMs)
            .tween('text', function tweenFn(d) {
              const i = d3.interpolateRound(d.value, coveragePercentage);
              return (t) => {
                // `this` object is coming from the tweenFn function. It contains the SVGTextElement.
                // eslint-disable-next-line react/no-this-in-sfc
                this.textContent = `${i(t)}%`;
              };
            });
        }
      }
    },
    [
      isCoverageMeasureComplete,
      coveragePercentage,
      innerRadius,
      outerRadius,
      cornerRadius,
      disableAnimation,
      isDropDownShown,
    ],
  );

  return (
    <Svg size={size}>
      <g ref={gRef} style={{ transform: 'translate(50%, 50%)' }}>
        <circle
          style={{
            stroke: tokens.color.ui_border,
            strokeWidth: 6,
            fill: 'none',
          }}
          r={innerRadius + arcWidth / 2}
        />
        <path />
        <text
          dominantBaseline="middle"
          textAnchor="middle"
          data-testid="traffic-widget-gauge-sumup-text"
          style={{ fill: textColor, ...tokens.getTextStyleProperties('header5') }}
        />
      </g>
    </Svg>
  );
};
